import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useI18n, LANGS } from "@/i18n/I18nContext";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const Logo = ({ className = "" }) => (
  <svg width="34" height="34" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} aria-hidden>
    <path d="M8 10H32L12 30H32" stroke="currentColor" strokeWidth="3.5" strokeLinecap="square" strokeLinejoin="miter"/>
    <path d="M8 10V22M32 18V30" stroke="#C27835" strokeWidth="2.5" strokeLinecap="square"/>
  </svg>
);

export default function Header() {
  const { lang, t, switchLang } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const base = `/${lang}`;

  const items = [
    { href: `${base}#what`, label: t("nav.what") },
    { href: `${base}#how`, label: t("nav.how") },
    { href: `${base}#projects`, label: t("nav.projects") },
    { href: `${base}#trust`, label: t("nav.trust") },
    { href: `${base}#contact`, label: t("nav.contact") },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#F8F7F4]/85 backdrop-blur-md border-b border-[#E5E3DC]/70" : "bg-transparent"
      }`}
      data-header
      style={{ wordBreak: "normal" }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <NavLink to={base} data-testid="header-logo-link" className="flex items-center gap-3 text-[#121316]">
          <Logo />
          <span className="font-serif text-xl tracking-[0.14em] uppercase">Zantravela</span>
        </NavLink>

        <nav className="hidden lg:flex items-center gap-9 text-[13px] font-medium text-[#121316]">
          {items.map((it) => (
            <a
              key={it.href}
              href={it.href}
              data-testid={`nav-link-${it.href.split("#")[1]}`}
              className="relative uppercase tracking-[0.14em] text-[11.5px] hover:text-[#C27835] transition-colors duration-200"
            >
              {it.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            role="group"
            aria-label={t("nav.language")}
            className="hidden sm:flex items-center rounded-full border border-[#E5E3DC] bg-white/60 backdrop-blur-sm p-0.5"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`language-switcher-${l}`}
                onClick={() => switchLang(l)}
                className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest rounded-full transition-colors duration-200 ${
                  lang === l
                    ? "bg-[#121316] text-[#F8F7F4]"
                    : "text-[#52535A] hover:text-[#121316]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#E5E3DC] bg-white/60 text-[#121316]"
            onClick={() => setOpen(true)}
            aria-label={t("nav.menu")}
            data-testid="header-menu-open"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="absolute inset-0 bg-[#121316]/50" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ y: "-4%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "-4%", opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-[#F8F7F4] border-b border-[#E5E3DC]"
            >
              <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
                <NavLink to={base} className="flex items-center gap-3 text-[#121316]">
                  <Logo />
                  <span className="font-serif text-xl tracking-[0.14em] uppercase">Zantravela</span>
                </NavLink>
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#E5E3DC] bg-white text-[#121316]"
                  onClick={() => setOpen(false)}
                  aria-label={t("nav.close")}
                  data-testid="header-menu-close"
                >
                  <X size={20} />
                </button>
              </div>
              <nav className="max-w-7xl mx-auto px-5 sm:px-8 pb-10 pt-4 grid gap-2">
                {items.map((it, idx) => (
                  <motion.a
                    key={it.href}
                    href={it.href}
                    initial={{ y: 16, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.04 * idx, ease: [0.16, 1, 0.3, 1] }}
                    className="font-serif text-4xl sm:text-5xl text-[#121316] py-2 border-b border-[#E5E3DC]/70"
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${it.href.split("#")[1]}`}
                  >
                    {it.label}
                  </motion.a>
                ))}
                <div className="mt-6 flex items-center gap-2">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      onClick={() => switchLang(l)}
                      data-testid={`mobile-lang-${l}`}
                      className={`px-4 py-2 rounded-full text-[11px] font-mono uppercase tracking-widest border transition-colors duration-200 ${
                        lang === l
                          ? "bg-[#121316] text-[#F8F7F4] border-[#121316]"
                          : "text-[#52535A] border-[#E5E3DC] hover:text-[#121316]"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </nav>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
